declare global {
  interface Window {
    FrontChat: (command: string, callback?: (value: any) => void) => void
  }
}

import { Inertia } from '@inertiajs/inertia'
import { Component, Fragment, h, Host, Method, Prop, State, Watch } from '@stencil/core'
import { gsap } from 'src/lib/gsap'
import { GetFathomBadge } from 'src/share/ui'

import { IBanner, ILayoutProps, SharedProps } from '../app'
import { Banner, dismissBanner, fetchPoints, GameButton, GameStore, showPoints } from '../gamification/ui'
import { InertiaForm } from '../inertia/form/form'
import { InertiaLink } from '../inertia/link/link'
import { IUser } from '../page/admin/types'
import { classes } from '../utils/css'
import { reloadInertiaPropsBulk } from '../utils/inertia'
import { sendLogs } from '../utils/new_relic'
import { BannerModal } from './modals'
import { AvatarMenu, GlobalNavMenuButton, GlobalNavStore, InviteButton } from './nav'
import { AiSearchField, SearchField, SearchStore } from './search'
import { flashToast, ToastStore } from './toast'

@Component({
  tag: 'app-layout',
})
export class Layout implements ILayoutProps {
  @Prop() env: SharedProps['env']
  @Prop() newRelicLogsUrl: string
  @Prop() logoutUrl: string
  @Prop() loginUrl?: string
  @Prop() currentVerifiedEmail?: string
  @Prop() homeUrl: string
  @Prop() appDownloadUrl: string
  @Prop() customizeUrl: string
  @Prop() startTestCallUrl: string
  @Prop() tutorialUrl: string
  @Prop() scheduleCallUrl: string
  @Prop() currentUser: IUser
  @Prop({ mutable: true }) flash: ILayoutProps['flash']
  @Prop() showHeader = true
  @Prop() showLoader = false
  @Prop() isInvestor = false
  @Prop() aiSearchCreateQueryUrl: string
  @Prop() adminConfig?: SharedProps['adminConfig']
  @Prop() searchSummaryUrl: string
  @Prop() bannerDismissUrl: string
  @Prop() pointsUrl: string
  @Prop() layoutPusherConfig: ILayoutProps['layoutPusherConfig'] = []
  @Prop() pusherClient: typeof Pusher

  @Prop() submitHelpRequestUrl: string
  @Prop() submitFeedbackUrl: string

  @Prop() banner: IBanner

  @State() useFrontChat = false
  @State() frontChatOpen = false

  loaderEl: HTMLUiLoaderElement

  @Watch('showLoader')
  animateLoader() {
    gsap.to(this.loaderEl, {
      opacity: this.showLoader ? 1 : 0,
      duration: this.showLoader ? 0 : 0.3,
      onStart: () => (this.loaderEl.hidden = false),
      onComplete: () => (this.loaderEl.hidden = !this.showLoader),
    })
  }

  // This is called on initial load and when Inertia updates the banner
  // We don't want to do anything automatically unless it's a prompt
  // Since if it's a success, we want the user to click the dialog/modal
  @Watch('banner')
  async updateBanner() {
    if (this.banner && this.banner.isGamificationOnly && !GameStore.state.isEnabled) {
      await dismissBanner(this.banner)
      return
    }

    if (this.banner && this.banner.type === 'prompt') {
      // Order of the next two actions is important
      await this.updatePoints(true)

      // Auto-dismiss banners
      if (this.banner.autoDismiss) {
        await dismissBanner(this.banner)
      }
    }
  }

  @Watch('pusherClient')
  @Watch('layoutPusherConfig')
  initPusher() {
    if (this.pusherClient && this.layoutPusherConfig.length) {
      const channelClientMap = {}
      this.layoutPusherConfig.map(({ channel, event }) => {
        let c = channelClientMap[channel]
        if (!c) {
          c = this.pusherClient.subscribe(channel)
          channelClientMap[channel] = c
        }
        c.bind(event, (data) => {
          sendLogs(this.newRelicLogsUrl, {
            pusher: {
              action: 'receive',
              channel: channel,
              event: event,
              data: data,
            },
            frontend: {
              version: this.env.APP_VERSION,
              component: 'app-layout',
            },
          })
          if (event.startsWith('gamification')) {
            reloadInertiaPropsBulk('app-layout', ['currentUser']).then((data) => {
              for (const [prop, propVal] of Object.entries(data)) {
                this[prop] = propVal
              }
            })
            this.updatePoints(false)
          } else if (event.startsWith('zoom')) {
            reloadInertiaPropsBulk('app-layout', ['currentUser', 'banner']).then((data) => {
              for (const [prop, propVal] of Object.entries(data)) {
                this[prop] = propVal
              }
            })
          }
        })
      })
    }
  }

  @Method()
  async updatePoints(withBanner: boolean) {
    await fetchPoints(withBanner)
  }

  async componentWillLoad() {
    GameStore.state.dismiss_url = this.bannerDismissUrl
    GameStore.state.points_url = this.pointsUrl
    this.flash.map(flashToast)
    await this.updatePoints(false)

    if ('FrontChat' in window) {
      this.useFrontChat = true
      this.setupFrontChat()
    }
  }

  componentDidLoad() {
    SearchStore.state.url = this.searchSummaryUrl
    if (this.showLoader) {
      this.animateLoader()
    }
    this.initPusher()
    this.updateBanner()
  }

  setupFrontChat() {
    window.FrontChat('onWindowVisibilityChanged', (value) => {
      this.frontChatOpen = value.is_window_visible
    })
  }

  toggleFrontChat() {
    if (this.frontChatOpen) {
      window.FrontChat('hide')
    } else {
      window.FrontChat('show')
    }
  }

  render() {
    return (
      <Host class='flex overflow-hidden relative flex-col'>
        {this.adminConfig?.isImpersonating && (
          <impersonation-popover
            impersonatedUserEmail={this.currentUser.email}
            stopImpersonatingUrl={this.adminConfig.stopImpersonatingUrl}
          />
        )}

        {!this.currentUser && this.showHeader && (
          <Fragment>
            {this.banner && this.banner.type !== 'success' ? <Banner banner={this.banner} /> : null}
            <header
              class='z-menu'
              style={{
                boxShadow: '0px 14px 24px 0px #00000040',
              }}
            >
              <section
                class={`
                bg-surface-low relative pl-4 sm:pl-6 flex justify-between items-center spacing-x-2 w-full h-10 text-glyph-base
              `}
              >
                <div class='flex items-center spacing-x-1'>
                  <a href='https://fathom.video/' target='_blank'>
                    <ui-logo size={20} />
                  </a>
                  <GetFathomBadge />
                </div>
                {this.currentVerifiedEmail ? (
                  <AvatarMenu currentVerifiedEmail={this.currentVerifiedEmail} logoutUrl={this.logoutUrl} />
                ) : (
                  <InertiaLink class='pr-6 font-extrabold' href={this.loginUrl}>
                    Sign In
                  </InertiaLink>
                )}
              </section>
            </header>
          </Fragment>
        )}

        {this.currentUser && this.showHeader && (
          <Fragment>
            {this.banner && this.banner.type !== 'success' ? <Banner banner={this.banner} /> : null}
            <header
              class='z-menu'
              style={{
                boxShadow: '0px 14px 24px 0px #00000040',
              }}
            >
              <section
                class={`
                  bg-surface-low relative pl-4 sm:pl-6 flex items-center spacing-x-2 w-full h-10 text-glyph-base
                `}
              >
                <InertiaLink href={this.homeUrl}>
                  <ui-logo class='text-base sm:text-xl' showInvestorLogo={this.isInvestor} />
                  <ui-tooltip on='hover' anchor='bottom'>
                    {this.isInvestor ? 'Thanks for investing in Fathom!' : 'Click to return home'}
                  </ui-tooltip>
                </InertiaLink>
                {this.aiSearchCreateQueryUrl ? (
                  <AiSearchField
                    class='flex-1 ml-1 sm:ml-3 sm:max-w-sm'
                    aiSearchCreateQueryUrl={this.aiSearchCreateQueryUrl}
                  />
                ) : (
                  <SearchField class='flex-1 ml-1 sm:ml-3 sm:max-w-xs' />
                )}
                <menu class='hidden sm:flex justify-end items-center p-0 pr-1 ml-auto h-full spacing-x-1'>
                  {this.currentUser.invite_url && (
                    <InviteButton
                      inviteUrl={this.currentUser.invite_url}
                      isTeamUser={this.currentUser.team?.isMember}
                    />
                  )}
                  <GlobalNavMenuButton href={this.customizeUrl} icon='gear'>
                    Settings
                  </GlobalNavMenuButton>
                  {this.useFrontChat ? (
                    <GlobalNavMenuButton
                      icon='life-preserver'
                      label='Help'
                      onClick={() => this.toggleFrontChat()}
                    ></GlobalNavMenuButton>
                  ) : (
                    <GlobalNavMenuButton icon='life-preserver' label='Help'>
                      <app-messaging-menu
                        tutorialUrl={this.tutorialUrl}
                        submitUrl={this.submitHelpRequestUrl}
                        title='Send message'
                        placeholder='What can we assist you with?'
                        renderSuccess={() => (
                          <Fragment>
                            <h3 class='flex items-center font-black text-link uppercase spacing-x-2'>
                              <span class='flex items-center text-4xl'>
                                <ui-icon icon='filter-right' class='-mr-3px text-xl' />
                                <ui-icon icon='envelope' />
                              </span>
                              <span class='text-xl'>Message sent</span>
                            </h3>
                            <p class='text-sm text-glyph-lighter whitespace-normal'>
                              We'll email you back shortly, usually under an hour but definitely within 24 hours.
                            </p>
                            <em class='not-italic text-surface-highest whitespace-normal'>
                              Replies sent to <span class='text-glyph-lightest'>{this.currentUser.email}</span>
                            </em>
                          </Fragment>
                        )}
                      ></app-messaging-menu>
                    </GlobalNavMenuButton>
                  )}
                  {GameStore.state.isEnabled && (
                    <GameButton hidden={GameStore.state.badgePoints <= 0} earnedPoints={GameStore.state.badgePoints} />
                  )}
                </menu>
                <AvatarMenu
                  currentUser={this.currentUser}
                  logoutUrl={this.logoutUrl}
                  appDownloadUrl={this.appDownloadUrl}
                  startTestCallUrl={this.startTestCallUrl}
                  tutorialUrl={this.tutorialUrl}
                  adminConfig={this.adminConfig}
                />
              </section>
              {GlobalNavStore.state.subnav && (
                <section
                  class='flex relative items-center pr-3 pl-6 w-full h-10 text-lg text-glyph-base bg-surface-low border-t'
                  style={{ borderColor: '#1A1A1A' }}
                >
                  {GlobalNavStore.state.subnav}
                </section>
              )}
            </header>
          </Fragment>
        )}
        <main class='flex overflow-hidden relative flex-col flex-1 text-foreground-0' style={{ background: '#1A1A1A' }}>
          <slot />
          <div class='flex fixed right-0 bottom-0 z-50 gap-4 items-end mr-5 text-xs shadow-lg'>
            {['development', 'staging'].includes(this.env.RAILS_ENV) && (
              <div class='flex items-center py-1 px-3 mb-2 font-mono text-glyph-lightest bg-black rounded-full border border-surface-high'>
                {this.env.APP_VERSION}
                <button class='inline-flex p-1 ml-1 align-middle'>
                  <ui-icon icon='sliders' size='lg' />
                  <ui-tooltip on='click' persist anchor='top'>
                    <menu class='flex flex-col p-0 m-0 text-left spacing-y-2'>
                      <InertiaForm method='post' action={this.logoutUrl}>
                        <input type='hidden' name='_method' value='delete' />
                        <button type='submit'>
                          <span>Sign Out</span>
                        </button>
                      </InertiaForm>
                    </menu>
                  </ui-tooltip>
                </button>
              </div>
            )}

            {this.submitFeedbackUrl && this.showHeader && (
              <button class='flex items-center p-3 text-xl font-bold leading-none text-surface-base bg-info-warn rounded-t-lg'>
                <ui-icon icon='lightning' /> <span class='ml-1'>Feedback</span>
                <app-messaging-menu
                  submitUrl={this.submitFeedbackUrl}
                  tutorialUrl={this.tutorialUrl}
                  title='Send feedback'
                  placeholder='We love feedback! How can we make Fathom even better for you?'
                  renderSuccess={() => (
                    <div class='flex flex-col items-center spacing-y-2'>
                      <h3 class='flex items-center font-black text-link uppercase spacing-x-2'>
                        <span class='flex items-center text-4xl'>
                          <ui-icon icon='filter-right' class='-mr-3px text-xl' />
                          <ui-icon icon='envelope' />
                        </span>
                        <span class='text-xl'>Feedback sent</span>
                      </h3>
                      <p class='text-sm text-glyph-lighter whitespace-normal'>
                        <strong>Thank you!</strong> We can't always respond but we read every piece of feedback.
                      </p>
                      <ui-peeps class='w-2/3 text-4xl' />
                    </div>
                  )}
                ></app-messaging-menu>
              </button>
            )}
          </div>
        </main>
        <div
          class={`
            absolute transition duration-200 inset-0 flex flex-col pointer-events-none spacing-y-2
            ${classes(this.showHeader, 'px-4 pt-16 mt-1 items-end z-menu', 'px-1 items-center z-50')}
            ${classes(!this.showHeader && ToastStore.state.toasts.length, 'bg-surface-lower pointer-events-auto mb-10')}
          `}
          style={{ '--tw-bg-opacity': '0.9' }}
        >
          {ToastStore.state.toasts.map((props) => (
            <ui-toast {...props} />
          ))}
        </div>
        {!this.adminConfig?.isImpersonating && (
          <BannerModal
            currentUser={this.currentUser}
            banner={this.banner}
            scheduleCallUrl={this.scheduleCallUrl}
            startTestCallUrl={this.startTestCallUrl}
            onClosed={async () => {
              // Banners sometimes can get double closed and it will be nulled
              if (this.banner) {
                const banner = this.banner
                this.banner = null
                await this.updatePoints(true)
                await dismissBanner(banner)

                if (banner.type === 'prompt' && banner.messageType === 'completed_onboarding') {
                  // Reload the page to get the next non-modal banner for post-onboarding
                  Inertia.reload()
                } else {
                  // Show the points (in most cases, what the modal is meant to trigger)
                  showPoints()
                }
              }
            }}
          />
        )}
        <ui-loader
          hidden
          ref={(el) => (this.loaderEl = el)}
          class='flex fixed top-0 left-0 z-menu justify-center items-center w-screen h-screen bg-surface-base'
          style={{ fontSize: '40px', zIndex: '2147483647' }}
        />
      </Host>
    )
  }
}
